import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Alert,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { TreatmentStatus } from "../../../../utils/enums";
import { LevelSelectForm } from "../../../../../../../components/LevelSelectForm/LevelSelectForm";

export default function AssessmentOverrideModal({
  open,
  onClose,
  onSave,
  isEditable,
  risk,
  treatments,
  theme,
}) {
  const [editedResidualScore, setEditedResidualScore] = useState(
    risk.residual_risk_score
  );
  const [validationError, setValidationError] = useState("");
  const [comment, setComment] = useState(risk.comments || "");
  const [treatment, setTreatment] = useState(
    risk?.treatment_title || TreatmentStatus.ACCEPTED
  );

  const SCORE_OPTIONS = Array.from({ length: 25 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }));

  const treatmentOptions = treatments.map((t) => ({
    value: t?.title,
    label: t?.title,
    description: t?.description,
  }));

  useEffect(() => {
    setEditedResidualScore(risk?.residual_risk_score);
    setComment(risk?.comments || "");
    setTreatment(
      treatments.find(
        (t) => t?.title === (risk?.treatment_title || TreatmentStatus.ACCEPTED)
      )?.title ||
      treatments[0]?.title ||
      ""
    );
    setValidationError("");
  }, [open, risk, treatments]);

  const handleSave = () => {
    if (!comment.trim()) {
      setValidationError("Comment is required");
      return;
    }
    if (!treatment) {
      setValidationError("Treatment is required");
      return;
    }
    onSave({
      residual_risk_score: parseInt(editedResidualScore),
      treatment,
      comment,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disabled={!isEditable}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Typography
          variant="h5"
          fontWeight={600}
          color={theme.customColors.text.primary}
        >
          Edit Residual Risk Score
        </Typography>
        <Typography variant="body2" color={theme.customColors.text.secondary}>
          Adjust the residual risk score
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            <LevelSelectForm
              label="Residual Risk Score"
              options={SCORE_OPTIONS}
              value={editedResidualScore}
              onChange={setEditedResidualScore}
              disabled={!isEditable}
            />
            <Typography
              variant="caption"
              color={theme.customColors.text.secondary}
            >
              1 (Lowest) to 25 (Highest)
            </Typography>
          </Box>
          <LevelSelectForm
            label="Treatment"
            options={treatmentOptions}
            value={treatment}
            onChange={setTreatment}
            disabled={!isEditable}
          />
          <TextField
            label="Treatment Note"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
          />
          {editedResidualScore > risk.inherent_risk_score && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="caption">
                Warning: Residual score ({editedResidualScore}) is greater than
                inherent score ({risk.inherent_risk_score})
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<CancelIcon />}
          sx={{ fontSize: "0.875rem" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={
            editedResidualScore > risk?.inherent_risk_score ||
            !comment.trim() ||
            !treatment
          }
          sx={{
            fontSize: "0.875rem",
            bgcolor: theme.customColors.button.dark,
            "&:hover": { bgcolor: theme.customColors.button.hover },
            "&:disabled": { bgcolor: theme.customColors.text.disabled },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
